import axios from '../interceptors/axios';
import { url } from './confserveur';

/**
 * Convertit une chaîne base64 en Blob
 * @param {string} base64Data - Données base64 (avec préfixe data:)
 * @returns {Blob}
 */
const base64ToBlob = (base64Data) => {
    const matches = base64Data.match(/^data:(.+);base64,(.+)$/);
    if (!matches) {
        throw new Error('Format base64 invalide');
    }

    const mimeType = matches[1];
    const base64 = matches[2];

    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);

    return new Blob([byteArray], { type: mimeType });
};

/**
 * Extrait l'extension du fichier depuis le type MIME ou le nom de fichier
 */
const getFileExtension = (base64Data, fileName) => {
    if (fileName) {
        const ext = fileName.split('.').pop();
        if (ext) return ext;
    }

    const mimeMatch = base64Data.match(/^data:image\/(\w+);/);
    if (mimeMatch) {
        return mimeMatch[1] === 'jpeg' ? 'jpg' : mimeMatch[1];
    }

    return 'png';
};

/**
 * Upload une image vers Cloudflare R2 via URL présignée
 *
 * @param {string} base64Data - Image en base64 (format data:image/xxx;base64,...)
 * @param {string} fileName - Nom du fichier original (optionnel)
 * @returns {Promise<string>} - URL publique de l'image sur R2
 */
export const uploadImageToR2 = async (base64Data, fileName = 'image.png') => {
    if (!base64Data || !base64Data.startsWith('data:')) {
        throw new Error('Données image invalides');
    }

    try {
        // 1. Convertir base64 en Blob d'abord pour connaître le content type exact
        const blob = base64ToBlob(base64Data);
        const contentType = blob.type;

        // 2. Obtenir l'URL présignée depuis le backend avec le content type exact
        const extension = getFileExtension(base64Data, fileName);
        const finalFileName = fileName || `image.${extension}`;

        // Utilise axios pour bénéficier du refresh token automatique
        const presignedResponse = await axios.get(
            `${url}questions/generate_upload_url/?filename=${encodeURIComponent(finalFileName)}&content_type=${encodeURIComponent(contentType)}`
        );

        const { uploadUrl, publicUrl } = presignedResponse.data;

        // 3. Uploader directement vers R2 (sans header Content-Type pour R2)
        const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: blob,
        });

        if (!uploadResponse.ok) {
            throw new Error('Échec de l\'upload vers R2');
        }

        // 4. Retourner l'URL publique
        return publicUrl;

    } catch (error) {
        console.error('Erreur upload R2:', error);
        throw error;
    }
};

/**
 * Upload toutes les images des éléments additionnels vers R2
 * Remplace les imageData (base64) par des imageUrl (URL publique)
 *
 * @param {Array} elements - Liste des éléments additionnels
 * @returns {Promise<Array>} - Éléments avec URLs au lieu de base64
 */
export const uploadElementsImagesToR2 = async (elements) => {
    if (!elements || elements.length === 0) {
        return elements;
    }

    const uploadedElements = await Promise.all(
        elements.map(async (element) => {
            // Traiter uniquement les éléments de type 'image' avec des données base64
            if (element.type === 'image' && element.config?.imageData) {
                try {
                    const publicUrl = await uploadImageToR2(
                        element.config.imageData,
                        element.config.fileName
                    );

                    // Retourner l'élément avec l'URL au lieu du base64
                    return {
                        ...element,
                        config: {
                            ...element.config,
                            imageUrl: publicUrl,
                            imageData: null, // Ne pas stocker le base64 en DB
                        }
                    };
                } catch (error) {
                    console.error('Erreur upload image élément:', error);
                    // En cas d'erreur, garder le base64 comme fallback
                    return element;
                }
            }

            return element;
        })
    );

    return uploadedElements;
};

export default uploadImageToR2;
